import channelDefinitions from '@nearst/channel-definitions';

// Per-channel visibility tiers, mirroring the backend visibility gate at
// interfaces/library/api-handlers/src/handlers/inventory/lib/visibility-gate.js.
// We resolve `issuesChannelId` here too so a channel that shares another's
// issues profile (e.g. Local Ads → Google) inherits the same tier without
// needing to be re-listed below.
export const SELF_SOURCING_CHANNELS = new Set(['google', 'local-ads']);
export const TITLE_FLOOR_CHANNELS = new Set(['storefront', 'product-reservations', 'local-checkout']);
export const resolveIssuesChannelId = (id) => channelDefinitions[id]?.issuesChannelId ?? id;

// One of 'visible' | 'blocked' | 'hidden'. Retailer-hidden and product-hidden
// both collapse to 'hidden'; Self-sourcing channels always render 'visible' if
// enabled; the title-floor channels' 'blocked' key is the title issue alone;
// every other channel blocks on any issue.
export function computeChannelVisibility(channelId, on, record) {
	if (!record) return 'hidden';
	if (record.hidden || !on) return 'hidden';
	const id = resolveIssuesChannelId(channelId);
	if (SELF_SOURCING_CHANNELS.has(id)) return 'visible';
	const issues = record.issues || [];
	if (TITLE_FLOOR_CHANNELS.has(id)) {
		return issues.includes('MissingProductTitle') ? 'blocked' : 'visible';
	}
	return issues.length > 0 ? 'blocked' : 'visible';
}

// Aggregate per-channel visibility across the shop's enabled channels.
// Returns `{ visible, blocked, total }` where:
//   - visible = enabled channels the product is currently visible on
//   - blocked = enabled channels the product is on but blocked by data issues
//   - total   = enabled channels the product is on (visible + blocked); channels
//               the retailer manually hid for this product are excluded from
//               all three, so the split pill can't mis-attribute a manual hide
//               to a data issue.
// Callers pass the Manage list's `record` shape (Typesense doc) — `channels` is
// an array of channel ids the product is enabled for. `hidden` is derived per
// row via `enabled_i === false`.
export function computeVisibilityCounts(record, shopEnabledChannels) {
	if (!record || !Array.isArray(shopEnabledChannels)) return { visible: 0, blocked: 0, total: 0 };
	const productChannels = new Set(record.channels ?? []);
	const hidden = record.enabled_i === false;
	let visible = 0;
	let blocked = 0;
	for (const channelId of shopEnabledChannels) {
		if (!productChannels.has(channelId)) continue;
		const state = computeChannelVisibility(channelId, true, { ...record, hidden });
		if (state === 'visible') visible++;
		else if (state === 'blocked') blocked++;
	}
	return { visible, blocked, total: visible + blocked };
}
